import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import RichTextView from '../components/RichTextView';
import { isRichTextEmpty } from '../utils/richtext';
import type { ExamPaper, Question } from '../types/models';
import './examPaperPrint.css';

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
const roman = (i: number) => ROMAN[i] ?? String(i + 1);

/** "180 min" → "3 hrs"; falls back to hours+minutes, else minutes. */
function formatDuration(min: number): string {
  if (min % 60 === 0) return `${min / 60} hrs`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} hr ${m} min` : `${m} min`;
}

const bloomStr = (levels?: number[] | null) =>
  levels && levels.length ? levels.map(l => `L${l}`).join(',') : '';

// Either/or alternatives (same choiceGroupId) collapse into one numbered unit; everything else
// is a standalone question. Order is preserved from the question list.
type QGroup =
  | { kind: 'single'; q: Question }
  | { kind: 'choice'; items: Question[] };

function groupByChoice(qs: Question[]): QGroup[] {
  const out: QGroup[] = [];
  const seen = new Set<string>();
  for (const q of qs) {
    if (q.choiceGroupId) {
      if (seen.has(q.choiceGroupId)) continue;
      seen.add(q.choiceGroupId);
      const items = qs.filter(x => x.choiceGroupId === q.choiceGroupId)
        .slice().sort((a, b) => a.choiceOrder - b.choiceOrder);
      out.push({ kind: 'choice', items });
    } else {
      out.push({ kind: 'single', q });
    }
  }
  return out;
}

// A single rendered table row (or an "OR" divider between either/or alternatives).
type Row =
  | { or: true }
  | { or?: false; no: string; cell: JSX.Element; marks: string; co: string; bl: string };

// Turn one question (or either/or alternative) into one or more table rows — a multi-part
// question yields one row per part; a plain question yields a single row.
function leafRows(q: Question, label: string): Row[] {
  const co = q.courseOutcome ?? '';
  const bl = bloomStr(q.bloomLevels);
  const parts = q.parts ?? [];

  if (parts.length > 0) {
    return parts.map((p, idx) => ({
      no: idx === 0 ? label : '',
      marks: String(p.marks),
      co,
      bl,
      cell: (
        <>
          {idx === 0 && !isRichTextEmpty(q.prompt) && (
            <div className="qp-stem"><RichTextView html={q.prompt} /></div>
          )}
          <div className="qp-subq">
            <span className="qp-subq-label">({roman(idx)})</span>
            <div><RichTextView html={p.prompt} /></div>
          </div>
        </>
      ),
    }));
  }

  const isMcq = q.type !== 'Descriptive';
  return [{
    no: label,
    marks: String(q.marks),
    co,
    bl,
    cell: (
      <>
        {isRichTextEmpty(q.prompt) ? <span /> : <RichTextView html={q.prompt} />}
        {isMcq && q.options.length > 0 && (
          <ol className="qp-options" type="A">
            {q.options.map(o => <li key={o.id}>{o.text}</li>)}
          </ol>
        )}
      </>
    ),
  }];
}

function groupRows(g: QGroup, num: number): Row[] {
  if (g.kind === 'single') return leafRows(g.q, `${num}.`);
  const rows: Row[] = [];
  g.items.forEach((alt, i) => {
    rows.push(...leafRows(alt, `${num} (${i === 0 ? 'a' : 'b'})`));
    if (i < g.items.length - 1) rows.push({ or: true });
  });
  return rows;
}

function answerRule(requiredAnswers: number, questionCount: number, maxMarks: number): string {
  const rule = requiredAnswers > 0
    ? `Answer any ${requiredAnswers} of ${questionCount}`
    : 'Answer ALL Questions';
  return `${rule} · ${maxMarks} Marks`;
}

export function ExamPaperPrintPage() {
  const { examId, orgId } = useParams();
  const nav = useNavigate();
  const [paper, setPaper] = useState<ExamPaper | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!examId) return;
    api.getPaper(examId).then(setPaper).catch(e => setError(e.message));
  }, [examId]);

  const back = () => nav(orgId ? `/org/${orgId}/exams/${examId}` : `/author/${examId}`);

  if (error) return <div className="container"><div className="card error">{error}</div></div>;
  if (!paper) return <div className="container"><div className="card muted">Loading…</div></div>;

  // Build the section blocks with continuous question numbering across the whole paper.
  const sections = [...paper.sections].sort((a, b) => a.orderIndex - b.orderIndex);
  let counter = 0;
  const blocks = sections.map(sec => {
    const qs = paper.questions.filter(q => q.sectionId === sec.id).sort((a, b) => a.orderIndex - b.orderIndex);
    const rows: Row[] = [];
    for (const g of groupByChoice(qs)) { counter++; rows.push(...groupRows(g, counter)); }
    return { sec, rows };
  });
  // Questions not assigned to any section (rendered in a trailing, untitled table).
  const looseQs = paper.questions.filter(q => !q.sectionId).sort((a, b) => a.orderIndex - b.orderIndex);
  const looseRows: Row[] = [];
  for (const g of groupByChoice(looseQs)) { counter++; looseRows.push(...groupRows(g, counter)); }

  const cos = paper.courseOutcomes ?? [];
  const subjectLine = [paper.subjectCode, paper.subjectName].filter(Boolean).join(' & ');

  const renderTable = (rows: Row[]) => (
    <table className="qp-table">
      <thead>
        <tr>
          <th className="qp-th-no">Q. No</th>
          <th>Questions</th>
          <th className="qp-th-m">Marks</th>
          <th className="qp-th-c">CO</th>
          <th className="qp-th-b">BL</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => r.or
          ? <tr key={i}><td /><td className="qp-or-cell">OR</td><td /><td /><td /></tr>
          : (
            <tr key={i}>
              <td className="qp-no">{r.no}</td>
              <td className="qp-q">{r.cell}</td>
              <td className="qp-m">{r.marks}</td>
              <td className="qp-c">{r.co}</td>
              <td className="qp-b">{r.bl}</td>
            </tr>
          ))}
      </tbody>
    </table>
  );

  return (
    <div className="qp-screen">
      <div className="qp-toolbar">
        <button onClick={back} className="topbar-ghost">Back</button>
        <button className="primary" onClick={() => window.print()}>Download / Print PDF</button>
      </div>

      <div className="qp-paper">
        {/* Header */}
        <div className="qp-head-top">
          {paper.organizationLogoDataUrl
            ? <img className="qp-logo" src={paper.organizationLogoDataUrl} alt="Logo" />
            : <div className="qp-logo">LOGO</div>}
          <div className="qp-rollno">
            <div className="qp-rollno-label">Roll No.</div>
            <div className="qp-boxes">
              {Array.from({ length: 12 }).map((_, i) => <span key={i} className="qp-box" />)}
            </div>
          </div>
        </div>
        <div className="qp-head-center">
          {paper.organizationName && <div className="qp-univ">{paper.organizationName}</div>}
          <div className="qp-examname">{paper.title}</div>
          {paper.departmentName && <div className="qp-line">{paper.departmentName}</div>}
          {subjectLine && <div className="qp-subject">{subjectLine}</div>}
          {paper.regulation && <div className="qp-reg">({paper.regulation})</div>}
        </div>
        <div className="qp-head-meta">
          <span>Time: {formatDuration(paper.durationMinutes)}</span>
          <span>Max. Marks: {paper.totalMarks}</span>
        </div>

        {/* CO legend */}
        {cos.length > 0 && (
          <table className="qp-co">
            <tbody>
              {cos.map(c => (
                <tr key={c.code}>
                  <td className="qp-co-code">{c.code}</td>
                  <td>{c.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* BL legend */}
        <div className="qp-bl">
          <strong>BL – Bloom's Taxonomy Levels</strong>
          <div className="qp-bl-sub">
            (L1-Remembering, L2-Understanding, L3-Applying, L4-Analysing, L5-Evaluating, L6-Creating)
          </div>
        </div>

        {/* Instructions */}
        {paper.instructions && (
          <div className="qp-part-sub" style={{ marginTop: 10, fontStyle: 'normal' }}>{paper.instructions}</div>
        )}

        {/* Sections */}
        {blocks.map(({ sec, rows }) => (
          <div key={sec.id} className="qp-part">
            <div className="qp-part-title">{sec.title}</div>
            <div className="qp-part-sub">{answerRule(sec.requiredAnswers, sec.questionCount, sec.maxMarks)}</div>
            {sec.instructions && <div className="qp-part-sub">{sec.instructions}</div>}
            {renderTable(rows)}
          </div>
        ))}

        {looseRows.length > 0 && (
          <div className="qp-part">{renderTable(looseRows)}</div>
        )}
      </div>
    </div>
  );
}
